import React from 'react';

import COUNTRIES from '@stripe-internal/countries';

import type {
  CountryCode,
  AddressValueAdapter,
  AddressFieldCountryCode,
} from '@sail/ui';
import {AddressField as SailAddressField} from '@sail/ui';

import type {IntlHook} from '@sail/intl';
import {message, useIntl} from '@sail/intl';

const addressPlaceholderMessages = {
  address_line1: message({
    id: 'connectEmbedLib.addressField.address_line1',
    description: "Placeholder text for the 'Address line 1' address field",
    defaultMessage: 'Street address',
  }),
  address_line2: message({
    id: 'connectEmbedLib.addressField.address_line2',
    description: "Placeholder text for the 'Address line 2' address field",
    defaultMessage: 'Apartment, unit, or other',
  }),
  country: message({
    id: 'connectEmbedLib.addressField.country',
    description: "Placeholder text for the 'Country' address field",
    defaultMessage: 'Country',
  }),
  subregion: {
    area: message({
      id: 'connectEmbedLib.addressField.subregion.area',
      description: "Placeholder text for the 'Subregion.Area' address field",
      defaultMessage: 'Area',
    }),
    county: message({
      id: 'connectEmbedLib.addressField.subregion.county',
      description: "Placeholder text for the 'Subregion.County' address field",
      defaultMessage: 'County',
    }),
    department: message({
      id: 'connectEmbedLib.addressField.subregion.department',
      description:
        "Placeholder text for the 'Subregion.Department' address field",
      defaultMessage: 'Department',
    }),
    district: message({
      id: 'connectEmbedLib.addressField.subregion.district',
      description:
        "Placeholder text for the 'Subregion.District' address field",
      defaultMessage: 'District',
    }),
    do_si: message({
      id: 'connectEmbedLib.addressField.subregion.doSi',
      description: "Placeholder text for the 'Subregion.Do_si' address field",
      defaultMessage: 'Do Si',
    }),
    emirate: message({
      id: 'connectEmbedLib.addressField.subregion.emirate',
      description: "Placeholder text for the 'Subregion.Emirate' address field",
      defaultMessage: 'Emirate',
    }),
    island: message({
      id: 'connectEmbedLib.addressField.subregion.island',
      description: "Placeholder text for the 'Subregion.Island' address field",
      defaultMessage: 'Island',
    }),
    oblast: message({
      id: 'connectEmbedLib.addressField.subregion.oblast',
      description: "Placeholder text for the 'Subregion.Oblast' address field",
      defaultMessage: 'Oblast',
    }),
    parish: message({
      id: 'connectEmbedLib.addressField.subregion.parish',
      description: "Placeholder text for the 'Subregion.Parish' address field",
      defaultMessage: 'Parish',
    }),
    prefecture: message({
      id: 'connectEmbedLib.addressField.subregion.prefecture',
      description:
        "Placeholder text for the 'Subregion.Prefecture' address field",
      defaultMessage: 'Prefecture',
    }),
    province: message({
      id: 'connectEmbedLib.addressField.subregion.province',
      description:
        "Placeholder text for the 'Subregion.Province' address field",
      defaultMessage: 'Province',
    }),
    state: message({
      id: 'connectEmbedLib.addressField.subregion.state',
      description: "Placeholder text for the 'Subregion.State' address field",
      defaultMessage: 'State',
    }),
  },
  locality: {
    city: message({
      id: 'connectEmbedLib.addressField.locality.city',
      description: "Placeholder text for the 'Locality.City' address field",
      defaultMessage: 'City',
    }),
    district: message({
      id: 'connectEmbedLib.addressField.locality.district',
      description: "Placeholder text for the 'Locality.District' address field",
      defaultMessage: 'District',
    }),
    post_town: message({
      id: 'connectEmbedLib.addressField.locality.postTown',
      description:
        "Placeholder text for the 'Locality.Post_town' address field",
      defaultMessage: 'Post town',
    }),
    suburb: message({
      id: 'connectEmbedLib.addressField.locality.suburb',
      description: "Placeholder text for the 'Locality.Suburb' address field",
      defaultMessage: 'Suburb',
    }),
  },
  dependentLocality: {
    suburb: message({
      id: 'connectEmbedLib.addressField.dependentLocality.suburb',
      description:
        "Placeholder text for the 'DependentLocality.Suburb' address field",
      defaultMessage: 'Suburb',
    }),
    district: message({
      id: 'connectEmbedLib.addressField.dependentLocality.district',
      description:
        "Placeholder text for the 'DependentLocality.District' address field",
      defaultMessage: 'District',
    }),
    village_township: message({
      id: 'connectEmbedLib.addressField.dependentLocality.villageTownship',
      description:
        "Placeholder text for the 'DependentLocality.Village_township' address field",
      defaultMessage: 'Village or township',
    }),
    townland: message({
      id: 'connectEmbedLib.addressField.dependentLocality.townland',
      description:
        "Placeholder text for the 'DependentLocality.Townland' address field",
      defaultMessage: 'Townland',
    }),
  },
  postalCode: {
    postal: message({
      id: 'connectEmbedLib.addressField.postalCode.postal',
      description: "Placeholder text for the 'PostalCode.Postal' address field",
      defaultMessage: 'Postal code',
    }),
    eircode: message({
      id: 'connectEmbedLib.addressField.postalCode.eircode',
      description:
        "Placeholder text for the 'PostalCode.Eircode' address field",
      defaultMessage: 'Eircode',
    }),
    pin: message({
      id: 'connectEmbedLib.addressField.postalCode.pin',
      description: "Placeholder text for the 'PostalCode.Pin' address field",
      defaultMessage: 'PIN',
    }),
    zip: message({
      id: 'connectEmbedLib.addressField.postalCode.zip',
      description: "Placeholder text for the 'PostalCode.Zip' address field",
      defaultMessage: 'Zip code',
    }),
  },
  sortingCode: {
    sorting: message({
      id: 'connectEmbedLib.addressField.sortingCode.sorting',
      description:
        "Placeholder text for the 'SortingCode.Sorting' address field",
      defaultMessage: 'Sorting code',
    }),
    cedex: message({
      id: 'connectEmbedLib.addressField.sortingCode.cedex',
      description: "Placeholder text for the 'SortingCode.Cedex' address field",
      defaultMessage: 'CEDEX',
    }),
  },
};

// eslint-disable-next-line @stripe-internal/stripe/no-mixed-react-exports
export const subRegionOverrides = {
  CA: {
    AB: 'Alberta',
    BC: 'British Columbia / Colombie-Britannique',
    MB: 'Manitoba',
    NB: 'New Brunswick / Nouveau-Brunswick',
    NL: 'Newfoundland and Labrador / Terre-Neuve-et-Labrador',
    NS: 'Nova Scotia / Nouvelle-Écosse',
    ON: 'Ontario',
    PE: 'Prince Edward Island / Île-du-Prince-Édouard',
    QC: 'Quebec / Québec',
    SK: 'Saskatchewan',
    NT: 'Northwest Territories / Territoires du Nord-Ouest',
    NU: 'Nunavut',
    YT: 'Yukon',
  },
};

// eslint-disable-next-line @stripe-internal/stripe/no-mixed-react-exports
export const formatAddressPlaceholders = (intl: IntlHook) => ({
  address_line1: intl.formatMessage(addressPlaceholderMessages.address_line1),
  address_line2: intl.formatMessage(addressPlaceholderMessages.address_line2),
  country: intl.formatMessage(addressPlaceholderMessages.country),
  subregion: {
    area: intl.formatMessage(addressPlaceholderMessages.subregion.area),
    county: intl.formatMessage(addressPlaceholderMessages.subregion.county),
    department: intl.formatMessage(
      addressPlaceholderMessages.subregion.department,
    ),
    district: intl.formatMessage(addressPlaceholderMessages.subregion.district),
    do_si: intl.formatMessage(addressPlaceholderMessages.subregion.do_si),
    emirate: intl.formatMessage(addressPlaceholderMessages.subregion.emirate),
    island: intl.formatMessage(addressPlaceholderMessages.subregion.island),
    oblast: intl.formatMessage(addressPlaceholderMessages.subregion.oblast),
    parish: intl.formatMessage(addressPlaceholderMessages.subregion.parish),
    prefecture: intl.formatMessage(
      addressPlaceholderMessages.subregion.prefecture,
    ),
    province: intl.formatMessage(addressPlaceholderMessages.subregion.province),
    state: intl.formatMessage(addressPlaceholderMessages.subregion.state),
  },
  locality: {
    city: intl.formatMessage(addressPlaceholderMessages.locality.city),
    district: intl.formatMessage(addressPlaceholderMessages.locality.district),
    post_town: intl.formatMessage(
      addressPlaceholderMessages.locality.post_town,
    ),
    suburb: intl.formatMessage(addressPlaceholderMessages.locality.suburb),
  },
  dependentLocality: {
    suburb: intl.formatMessage(
      addressPlaceholderMessages.dependentLocality.suburb,
    ),
    district: intl.formatMessage(
      addressPlaceholderMessages.dependentLocality.district,
    ),
    village_township: intl.formatMessage(
      addressPlaceholderMessages.dependentLocality.village_township,
    ),
    townland: intl.formatMessage(
      addressPlaceholderMessages.dependentLocality.townland,
    ),
  },
  postalCode: {
    postal: intl.formatMessage(addressPlaceholderMessages.postalCode.postal),
    eircode: intl.formatMessage(addressPlaceholderMessages.postalCode.eircode),
    pin: intl.formatMessage(addressPlaceholderMessages.postalCode.pin),
    zip: intl.formatMessage(addressPlaceholderMessages.postalCode.zip),
  },
  sortingCode: {
    sorting: intl.formatMessage(addressPlaceholderMessages.sortingCode.sorting),
    cedex: intl.formatMessage(addressPlaceholderMessages.sortingCode.cedex),
  },
});

// eslint-disable-next-line @stripe-internal/stripe/no-mixed-react-exports
export const generateCountryIntlMap = (intl: IntlHook) => {
  const countryCodes = Object.keys(COUNTRIES) as AddressFieldCountryCode[];
  const intlMap = countryCodes.reduce((acc, code) => {
    acc[code] = intl.formatDisplayName(code, {type: 'region'});
    return acc;
  }, {} as Record<AddressFieldCountryCode, string>);
  return intlMap;
};

export type BackwardsCompatibleAddressValue = {
  country: string | null | undefined;
  line1: string | null | undefined;
  line2: string | null | undefined;
  city: string | null | undefined;
  state: string | null | undefined;
  postalCode: string | null | undefined;
};

export type AddressFieldProps = {
  label?: React.ReactNode;
  description?: React.ReactNode;
  disabled?: boolean;
  error?: React.ReactNode;
  size?: 'small' | 'medium' | 'large';
  onBlur?: () => void;
  onChange: (address: BackwardsCompatibleAddressValue) => void;
  values?: BackwardsCompatibleAddressValue;
};

export const AddressField = ({
  label,
  description,
  disabled,
  error,
  size = 'medium',
  onBlur,
  onChange,
  values,
}: AddressFieldProps) => {
  const intl = useIntl();
  const adapter: AddressValueAdapter<BackwardsCompatibleAddressValue> = {
    toAddressValue: (value) => {
      return {
        address: value.line1 || '',
        address2: value.line2 || '',
        locality: value.city || '',
        subregion: value.state || '',
        postalCode: value.postalCode || '',
        countryCode: (value.country || 'US') as CountryCode,
      };
    },
    fromAddressValue: (value) => {
      return {
        line1: value.address,
        line2: value.address2,
        city: value.locality,
        state: value.subregion,
        postalCode: value.postalCode,
        country: value.countryCode,
      };
    },
  };

  return (
    <SailAddressField
      disabled={disabled}
      size={size}
      label={label}
      description={description}
      error={error}
      onBlur={onBlur}
      onChange={(values) => {
        const val = adapter.fromAddressValue(values);
        onChange(val);
      }}
      defaultValue={values ? adapter.toAddressValue(values) : undefined}
      unstable_labelsOverrides={{
        address: formatAddressPlaceholders(intl),
        country: generateCountryIntlMap(intl),
        subregion: subRegionOverrides,
      }}
    />
  );
};
